import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import * as siteAuth from '../lib/siteAuth';

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/** 権限スラグ（API / Supabase と一致） */
export const PERMISSIONS = {
  user_manage: 'user_manage',
  schedule_manage: 'schedule_manage',
  booking_use: 'booking_use',
  profile_edit: 'profile_edit',
  booking_view: 'booking_view',
} as const;

export type AuthUser = {
  id: string;
  email: string;
  name: string | null;
  role: { id: number; name: string; slug: string } | null;
  permissions: string[];
};

/** ユーザーが指定権限を持つか */
export function can(user: AuthUser | null, permission: string): boolean {
  return Array.isArray(user?.permissions) && user.permissions.includes(permission);
}

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refetch: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

async function phpFetch(path: string, options: RequestInit = {}) {
  const url = API_BASE ? `${API_BASE}${path}` : path;
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
}

function toAuthUser(u: siteAuth.SiteAuthUser): AuthUser {
  return {
    id: u.id,
    email: u.email,
    name: u.name,
    role: u.role,
    permissions: u.permissions,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (isSupabaseConfigured()) {
      try {
        const siteUser = await siteAuth.getSiteSession();
        setUser(siteUser ? toAuthUser(siteUser) : null);
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!API_BASE) {
      setLoading(false);
      return;
    }
    try {
      const res = await phpFetch('/me.php');
      if (res.ok) {
        const data = await res.json();
        const u = data.user;
        if (u) {
          setUser({
            id: String(u.id),
            email: u.email ?? '',
            name: u.name ?? null,
            role: u.role ?? null,
            permissions: Array.isArray(u.permissions) ? u.permissions : [],
          });
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Supabase 利用時: 他タブでのログイン/ログアウトに追従
  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) return;
    const { data } = supabase.auth.onAuthStateChange(() => {
      refetch();
    });
    return () => data.subscription.unsubscribe();
  }, [refetch]);

  const login = useCallback(async (email: string, password: string) => {
    if (isSupabaseConfigured()) {
      const siteUser = await siteAuth.signInSite(email, password);
      setUser(toAuthUser(siteUser));
      return;
    }
    if (!API_BASE) {
      throw new Error('APIのURLが設定されていません。Supabase または PHP API のどちらかを設定してください。');
    }
    const res = await phpFetch('/login.php', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error ?? 'ログインに失敗しました。');
    }
    const u = data.user;
    setUser(
      u
        ? {
            id: String(u.id),
            email: u.email ?? '',
            name: u.name ?? null,
            role: u.role ?? null,
            permissions: Array.isArray(u.permissions) ? u.permissions : [],
          }
        : null
    );
  }, []);

  const logout = useCallback(async () => {
    if (isSupabaseConfigured()) {
      await siteAuth.signOutSite();
    } else if (API_BASE) {
      try {
        await phpFetch('/logout.php', { method: 'POST' });
      } catch {
        /* ignore */
      }
    }
    setUser(null);
  }, []);

  const value: AuthContextValue = { user, loading, login, logout, refetch };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
